import { useState, useCallback, useEffect, useRef } from 'react';
import { Collapse, Spin, Empty } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import ActiviteService from '../../services/ActiviteService';
import { catchError } from '../../services/DaoService';
import ActivityItem from '../Activity/ActivityItem';
import PropTypes from 'prop-types';

export default function ActiviteDoneSection({ filters, users, onToggleDone, onDelete }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(true);
  const sentinelRef = useRef(null);
  const filtersRef = useRef(filters);

  const doneFilters = useCallback(
    () => ({ ...filtersRef.current, statut: 'done' }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ActiviteService.getInstance().search(doneFilters());
      setList(res.data.data);
      setPagination(res.data);
      setDirty(false);
    } catch (e) {
      catchError(e);
    } finally {
      setLoading(false);
    }
  }, [doneFilters]);

  useEffect(() => { filtersRef.current = filters; });

  useEffect(() => {
    if (open) load();
    else setDirty(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.searchText, filters.type, filters.my_role]);

  const handleToggle = (keys) => {
    const isOpen = keys.includes('done');
    setOpen(isOpen);
    if (isOpen && dirty) load();
  };

  const loadMore = useCallback(async () => {
    if (!pagination?.next_page_url || loading) return;
    setLoading(true);
    try {
      const res = await ActiviteService.post(pagination.next_page_url, doneFilters());
      setList(prev => [...prev, ...res.data.data]);
      setPagination(res.data);
    } catch (e) {
      catchError(e);
    } finally {
      setLoading(false);
    }
  }, [pagination, loading, doneFilters]);

  useEffect(() => {
    if (!open) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, open]);

  const handleLocalToggle = (updated) => {
    setList(prev => prev.filter(a => a.id !== updated.id));
    onToggleDone(updated);
  };

  const handleLocalDelete = (activity) => {
    setList(prev => prev.filter(a => a.id !== activity.id));
    onDelete(activity);
  };

  const handleUpdate = (updated) => {
    setList(prev => prev.map(a => a.id === updated.id ? updated : a));
  };

  const total = pagination?.total ?? null;
  const label = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontSize: 13 }}>
      <CheckCircleFilled style={{ color: '#52C41A' }} />
      Terminées
      {total !== null && (
        <span style={{ fontSize: 12, color: '#8C9BAB', fontWeight: 400 }}>({total})</span>
      )}
    </span>
  );

  return (
    <Collapse
      onChange={handleToggle}
      ghost={false}
      style={{ borderTop: '1px solid var(--border-subtle, #F1F2F4)', borderRadius: 0 }}
      items={[{
        key: 'done',
        label,
        children: (
          <Spin spinning={loading && list.length === 0}>
            {list.length === 0 && !loading
              ? <div style={{ padding: '24px 0' }}><Empty description="Aucune activité terminée" /></div>
              : (
                <div>
                  {list.map(a => (
                    <div key={a.id} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                      <ActivityItem
                        item={a}
                        users={users}
                        onDelete={handleLocalDelete}
                        onToggleDone={handleLocalToggle}
                        onUpdate={handleUpdate}
                      />
                    </div>
                  ))}
                  <div ref={sentinelRef} style={{ height: 1 }} />
                  {loading && list.length > 0 && (
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                      <Spin size="small" />
                    </div>
                  )}
                </div>
              )
            }
          </Spin>
        ),
      }]}
    />
  );
}

ActiviteDoneSection.propTypes = {
  filters:      PropTypes.object.isRequired,
  users:        PropTypes.array.isRequired,
  onToggleDone: PropTypes.func.isRequired,
  onDelete:     PropTypes.func.isRequired,
};
